// Play Console → App content → Data safety, as a form instead of a CSV.
//
// The Android Publisher API takes the questionnaire as the CSV the console
// exports: one row per question (or per choice of a multiple-choice question),
// with `true` / `false` / empty in the value column. The row set is fixed and
// identical for every app — 782 rows in the September 2026 export — so the
// whole file can be generated from a small structured answer set, and read
// back into one. `toCsv` and `fromCsv` are inverses over that answer set.
//
// Question and choice ids are Google's. The English labels are the console's
// own; the label column is informational and the API ignores it, but a human
// reading the CSV should still understand it.

export interface DataTypeDef {
  id: string;
  label: string;
  /** Google's own hint under the type name in the console. */
  hint?: string;
}
export interface DataCategoryDef {
  /** Question id of the multiple-choice row group, e.g. PSL_DATA_TYPES_PERSONAL. */
  id: string;
  label: string;
  types: DataTypeDef[];
}

export const DATA_CATEGORIES: DataCategoryDef[] = [
  { id: 'PSL_DATA_TYPES_PERSONAL', label: 'Personal info', types: [
    { id: 'PSL_NAME', label: 'Name' },
    { id: 'PSL_EMAIL', label: 'Email address' },
    { id: 'PSL_USER_ACCOUNT', label: 'User IDs' },
    { id: 'PSL_ADDRESS', label: 'Address' },
    { id: 'PSL_PHONE', label: 'Phone number' },
    { id: 'PSL_RACE_ETHNICITY', label: 'Race and ethnicity' },
    { id: 'PSL_POLITICAL_RELIGIOUS', label: 'Political or religious beliefs' },
    { id: 'PSL_SEXUAL_ORIENTATION_GENDER_IDENTITY', label: 'Sexual orientation' },
    { id: 'PSL_OTHER_PERSONAL', label: 'Other info' },
  ] },
  { id: 'PSL_DATA_TYPES_FINANCIAL', label: 'Financial info', types: [
    { id: 'PSL_CREDIT_DEBIT_BANK_ACCOUNT_NUMBER', label: 'User payment info' },
    { id: 'PSL_PURCHASE_HISTORY', label: 'Purchase history' },
    { id: 'PSL_CREDIT_SCORE', label: 'Credit score' },
    { id: 'PSL_OTHER', label: 'Other financial info' },
  ] },
  { id: 'PSL_DATA_TYPES_LOCATION', label: 'Location', types: [
    { id: 'PSL_APPROX_LOCATION', label: 'Approximate location' },
    { id: 'PSL_PRECISE_LOCATION', label: 'Precise location' },
  ] },
  { id: 'PSL_DATA_TYPES_SEARCH_AND_BROWSING', label: 'Web browsing', types: [
    { id: 'PSL_WEB_BROWSING_HISTORY', label: 'Web browsing history' },
  ] },
  { id: 'PSL_DATA_TYPES_EMAIL_AND_TEXT', label: 'Messages', types: [
    { id: 'PSL_EMAILS', label: 'Emails' },
    { id: 'PSL_SMS_CALL_LOG', label: 'SMS or MMS' },
    { id: 'PSL_OTHER_MESSAGES', label: 'Other in-app messages' },
  ] },
  { id: 'PSL_DATA_TYPES_PHOTOS_AND_VIDEOS', label: 'Photos and videos', types: [
    { id: 'PSL_PHOTOS', label: 'Photos' },
    { id: 'PSL_VIDEOS', label: 'Videos' },
  ] },
  { id: 'PSL_DATA_TYPES_AUDIO', label: 'Audio files', types: [
    { id: 'PSL_AUDIO', label: 'Voice or sound recordings' },
    { id: 'PSL_MUSIC', label: 'Music files' },
    { id: 'PSL_OTHER_AUDIO', label: 'Other audio files' },
  ] },
  { id: 'PSL_DATA_TYPES_HEALTH_AND_FITNESS', label: 'Health and fitness', types: [
    { id: 'PSL_HEALTH', label: 'Health info' },
    { id: 'PSL_FITNESS', label: 'Fitness info' },
  ] },
  { id: 'PSL_DATA_TYPES_CONTACTS', label: 'Contacts', types: [
    { id: 'PSL_CONTACTS', label: 'Contacts' },
  ] },
  { id: 'PSL_DATA_TYPES_CALENDAR', label: 'Calendar', types: [
    { id: 'PSL_CALENDAR', label: 'Calendar events' },
  ] },
  { id: 'PSL_DATA_TYPES_APP_PERFORMANCE', label: 'App info and performance', types: [
    { id: 'PSL_CRASH_LOGS', label: 'Crash logs' },
    { id: 'PSL_PERFORMANCE_DIAGNOSTICS', label: 'Diagnostics' },
    { id: 'PSL_OTHER_PERFORMANCE', label: 'Other app performance data' },
  ] },
  { id: 'PSL_DATA_TYPES_FILES_AND_DOCS', label: 'Files and docs', types: [
    { id: 'PSL_FILES_AND_DOCS', label: 'Files and docs' },
  ] },
  { id: 'PSL_DATA_TYPES_APP_ACTIVITY', label: 'App activity', types: [
    { id: 'PSL_USER_INTERACTION', label: 'App interactions' },
    { id: 'PSL_IN_APP_SEARCH_HISTORY', label: 'In-app search history' },
    { id: 'PSL_APPS_ON_DEVICE', label: 'Installed apps' },
    { id: 'PSL_USER_GENERATED_CONTENT', label: 'Other user-generated content' },
    { id: 'PSL_OTHER_APP_ACTIVITY', label: 'Other actions' },
  ] },
  { id: 'PSL_DATA_TYPES_IDENTIFIERS', label: 'Device or other IDs', types: [
    { id: 'PSL_DEVICE_ID', label: 'Device or other IDs' },
  ] },
];

export const ALL_DATA_TYPES: DataTypeDef[] = DATA_CATEGORIES.flatMap((c) => c.types);

export const PURPOSES = [
  { id: 'PSL_APP_FUNCTIONALITY', label: 'App functionality' },
  { id: 'PSL_ANALYTICS', label: 'Analytics' },
  { id: 'PSL_DEVELOPER_COMMUNICATIONS', label: 'Developer communications' },
  { id: 'PSL_FRAUD_PREVENTION_SECURITY', label: 'Fraud prevention, security, and compliance' },
  { id: 'PSL_ADVERTISING', label: 'Advertising or marketing' },
  { id: 'PSL_PERSONALIZATION', label: 'Personalization' },
  { id: 'PSL_ACCOUNT_MANAGEMENT', label: 'Account management' },
] as const;
export type PurposeId = typeof PURPOSES[number]['id'];

export const ACCOUNT_METHODS = [
  { id: 'PSL_ACM_USER_ID_PASSWORD', label: 'Username and password' },
  { id: 'PSL_ACM_USER_ID_OTHER_AUTH', label: 'Username and other authentication' },
  { id: 'PSL_ACM_USER_ID_PASSWORD_OTHER_AUTH', label: 'Username, password and other authentication' },
  { id: 'PSL_ACM_OAUTH', label: 'OAuth (Google, Facebook, …)' },
  { id: 'PSL_ACM_OTHER', label: 'Other' },
] as const;
export type AccountMethodId = typeof ACCOUNT_METHODS[number]['id'];

export const OUTSIDE_ACCOUNT_TYPES = [
  { id: 'PSL_LOGIN_WITH_OUTSIDE_APP_ID', label: 'Identified outside the app (SIM card, device, …)' },
  { id: 'PSL_LOGIN_THROUGH_EMPLOYMENT_OR_ENTERPRISE_ACCOUNT', label: 'Through an employer or organization' },
  { id: 'PSL_OUTSIDE_APP_ACCOUNT_TYPE_OTHER', label: 'Other' },
] as const;
export type OutsideAccountTypeId = typeof OUTSIDE_ACCOUNT_TYPES[number]['id'];

export type DeletionAnswer = 'DATA_DELETION_YES' | 'DATA_DELETION_NO' | 'DATA_DELETION_NO_AUTO_DELETED';
export const DELETION_OPTIONS: Array<{ id: DeletionAnswer; label: string }> = [
  { id: 'DATA_DELETION_YES', label: 'Yes, users can request deletion' },
  { id: 'DATA_DELETION_NO', label: 'No' },
  { id: 'DATA_DELETION_NO_AUTO_DELETED', label: 'No, but data is deleted automatically' },
];

export interface DataTypeUsage {
  collected: boolean;
  shared: boolean;
  /** Processed ephemerally (only in memory, never stored). */
  ephemeral: boolean;
  /** true = users cannot opt out; false = collection is optional. */
  required: boolean;
  collectionPurposes: PurposeId[];
  sharingPurposes: PurposeId[];
}

export interface DataSafetyForm {
  collectsOrShares: boolean;
  encryptedInTransit: boolean;
  accountMethods: AccountMethodId[];
  /** Free text behind the "Other" account method. */
  accountMethodOther: string;
  accountDeletionUrl: string;
  deletion: DeletionAnswer;
  dataDeletionUrl: string;
  compliesFamilyPolicy: boolean;
  independentlyValidated: boolean;
  upiBadge: boolean;
  outsideAppAccounts: boolean;
  outsideAccountTypes: OutsideAccountTypeId[];
  outsideAccountTypeOther: string;
  /** Keyed by data type id; a key present means the type is declared. */
  types: Record<string, DataTypeUsage>;
}

export function emptyUsage(): DataTypeUsage {
  return { collected: true, shared: false, ephemeral: false, required: true, collectionPurposes: [], sharingPurposes: [] };
}

export function emptyDataSafety(): DataSafetyForm {
  return {
    collectsOrShares: false,
    encryptedInTransit: true,
    accountMethods: [],
    accountMethodOther: '',
    accountDeletionUrl: '',
    deletion: 'DATA_DELETION_NO',
    dataDeletionUrl: '',
    compliesFamilyPolicy: false,
    independentlyValidated: false,
    upiBadge: false,
    outsideAppAccounts: false,
    outsideAccountTypes: [],
    outsideAccountTypeOther: '',
    types: {},
  };
}

/** A casino game with AdMob and Firebase: what template 1 declares. */
export function defaultAdsAnalyticsDataSafety(): DataSafetyForm {
  const form = emptyDataSafety();
  form.collectsOrShares = true;
  form.types = {
    PSL_CRASH_LOGS: { ...emptyUsage(), collectionPurposes: ['PSL_APP_FUNCTIONALITY', 'PSL_ANALYTICS'] },
    PSL_PERFORMANCE_DIAGNOSTICS: { ...emptyUsage(), collectionPurposes: ['PSL_APP_FUNCTIONALITY', 'PSL_ANALYTICS'] },
    PSL_USER_INTERACTION: {
      ...emptyUsage(), shared: true,
      collectionPurposes: ['PSL_ANALYTICS', 'PSL_ADVERTISING'], sharingPurposes: ['PSL_ANALYTICS', 'PSL_ADVERTISING'],
    },
    PSL_DEVICE_ID: {
      ...emptyUsage(), shared: true,
      collectionPurposes: ['PSL_ANALYTICS', 'PSL_FRAUD_PREVENTION_SECURITY', 'PSL_ADVERTISING'],
      sharingPurposes: ['PSL_ANALYTICS', 'PSL_ADVERTISING'],
    },
  };
  return form;
}

/**
 * Everything that would make the console (or Google's review) reject the
 * questionnaire. Returned as messages, one per problem, so the form can show
 * them all at once.
 */
export function validateDataSafety(form: DataSafetyForm): string[] {
  const errors: string[] = [];
  const declared = Object.entries(form.types);
  if (form.collectsOrShares) {
    if (declared.length === 0) errors.push('Select at least one data type, or answer "No" to data collection.');
    for (const [id, u] of declared) {
      const label = ALL_DATA_TYPES.find((t) => t.id === id)?.label ?? id;
      if (!u.collected && !u.shared) errors.push(`${label}: choose collected, shared, or both.`);
      if (u.collected && !u.ephemeral && u.collectionPurposes.length === 0) errors.push(`${label}: pick at least one collection purpose.`);
      if (u.shared && u.sharingPurposes.length === 0) errors.push(`${label}: pick at least one sharing purpose.`);
    }
  } else if (declared.length > 0) {
    errors.push('Data types are declared but data collection is answered "No".');
  }
  if (form.accountMethods.includes('PSL_ACM_OTHER') && !form.accountMethodOther.trim()) {
    errors.push('Describe the "Other" account creation method.');
  }
  if (form.accountMethods.length > 0 && !form.accountDeletionUrl.trim()) {
    errors.push('Apps with accounts must give an account deletion URL.');
  }
  if (form.deletion === 'DATA_DELETION_YES' && !form.dataDeletionUrl.trim()) {
    errors.push('Give the URL where users request data deletion.');
  }
  if (form.outsideAppAccounts) {
    if (form.outsideAccountTypes.length === 0) errors.push('Say how outside-app accounts are created.');
    if (form.outsideAccountTypes.includes('PSL_OUTSIDE_APP_ACCOUNT_TYPE_OTHER') && !form.outsideAccountTypeOther.trim()) {
      errors.push('Describe the "Other" outside-app account type.');
    }
  }
  return errors;
}

// ---------------------------------------------------------------------------
// CSV

const HEADER = 'Question ID (machine readable),Response ID (machine readable),Response value,Answer requirement,Human-friendly question label';

type Row = [question: string, response: string, value: string, requirement: string, label: string];

function quote(s: string): string {
  return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

const b = (v: boolean) => (v ? 'true' : 'false');
/** A choice row: `true` when picked, empty when not (as the console exports). */
const pick = (v: boolean) => (v ? 'true' : '');

function usageRows(type: DataTypeDef, u: DataTypeUsage | undefined): Row[] {
  const q = (suffix: string) => `PSL_DATA_USAGE_RESPONSES:${type.id}:${suffix}`;
  const L = `Data usage and handling (${type.label})`;
  const rows: Row[] = [];
  // A declared type answers every choice with true/false; the rows of an
  // undeclared type are present but blank. Both match the console's export.
  const on = (v: boolean) => (u ? b(v) : '');
  const val = on;
  rows.push([q('PSL_DATA_USAGE_COLLECTION_AND_SHARING'), 'PSL_DATA_USAGE_ONLY_COLLECTED', on(!!u?.collected), 'MULTIPLE_CHOICE', `${L}/Collected`]);
  rows.push([q('PSL_DATA_USAGE_COLLECTION_AND_SHARING'), 'PSL_DATA_USAGE_ONLY_SHARED', on(!!u?.shared), 'MULTIPLE_CHOICE', `${L}/Shared`]);
  rows.push([q('PSL_DATA_USAGE_EPHEMERAL'), '', val(!!u?.ephemeral), 'MAYBE_REQUIRED', `${L}/Is this data processed ephemerally?`]);
  rows.push([q('DATA_USAGE_USER_CONTROL'), 'PSL_DATA_USAGE_USER_CONTROL_OPTIONAL', on(u ? !u.required : false), 'SINGLE_CHOICE', `${L}/Users can choose whether this data is collected`]);
  rows.push([q('DATA_USAGE_USER_CONTROL'), 'PSL_DATA_USAGE_USER_CONTROL_REQUIRED', on(!!u?.required), 'SINGLE_CHOICE', `${L}/Data collection is required (users can't turn off)`]);
  for (const p of PURPOSES) {
    rows.push([q('DATA_USAGE_COLLECTION_PURPOSE'), p.id, on(!!u?.collectionPurposes.includes(p.id)), 'MULTIPLE_CHOICE', `${L}/Why is this data collected?/${p.label}`]);
  }
  for (const p of PURPOSES) {
    rows.push([q('DATA_USAGE_SHARING_PURPOSE'), p.id, on(!!u?.sharingPurposes.includes(p.id)), 'MULTIPLE_CHOICE', `${L}/Why is this data shared?/${p.label}`]);
  }
  return rows;
}

export function toCsv(form: DataSafetyForm): string {
  const rows: Row[] = [];
  rows.push(['PSL_DATA_COLLECTION_COLLECTS_PERSONAL_DATA', '', b(form.collectsOrShares), 'REQUIRED', 'Does your app collect or share any of the required user data types?']);
  rows.push(['PSL_DATA_COLLECTION_ENCRYPTED_IN_TRANSIT', '', b(form.encryptedInTransit), 'MAYBE_REQUIRED', 'Is all of the user data collected by your app encrypted in transit?']);
  const acmQ = 'Which of the following account creation methods does your app support? Select all that apply.';
  for (const m of ACCOUNT_METHODS) {
    rows.push(['PSL_SUPPORTED_ACCOUNT_CREATION_METHODS', m.id, pick(form.accountMethods.includes(m.id)), 'MULTIPLE_CHOICE', `${acmQ}/${m.label}`]);
  }
  rows.push(['PSL_SUPPORTED_ACCOUNT_CREATION_METHODS', 'PSL_ACM_NONE', pick(form.accountMethods.length === 0), 'MULTIPLE_CHOICE', `${acmQ}/My app does not allow users to create an account`]);
  rows.push(['PSL_ACM_SPECIFY', '', form.accountMethodOther, 'MAYBE_REQUIRED', 'Describe the account creation method your app uses.']);
  rows.push(['PSL_ACCOUNT_DELETION_URL', '', form.accountDeletionUrl, 'MAYBE_REQUIRED', 'Provide a link where users can request that their account and associated data be deleted.']);
  for (const d of DELETION_OPTIONS) {
    rows.push(['PSL_SUPPORT_DATA_DELETION_BY_USER', d.id, pick(form.deletion === d.id), 'SINGLE_CHOICE', `Do you provide a way for users to request that their data is deleted?/${d.label}`]);
  }
  rows.push(['PSL_DATA_DELETION_URL', '', form.dataDeletionUrl, 'MAYBE_REQUIRED', 'Data deletion request URL']);
  rows.push(['PSL_DATA_COLLECTION_COMPLIES_FAMILY_POLICY', '', b(form.compliesFamilyPolicy), 'OPTIONAL', 'Only answer if your app targets children: does it comply with the Families policy?']);
  rows.push(['PSL_INDEPENDENTLY_VALIDATED', '', b(form.independentlyValidated), 'OPTIONAL', 'Has your app been independently validated against a global security standard?']);
  rows.push(['PSL_UPI_BADGE_OPT_IN', '', b(form.upiBadge), 'OPTIONAL', 'Do you want to show this badge on your Google Play store listing?']);
  rows.push(['PSL_HAS_OUTSIDE_APP_ACCOUNTS', '', b(form.outsideAppAccounts), 'OPTIONAL', 'Can users sign in with accounts created outside the app?']);
  for (const t of OUTSIDE_ACCOUNT_TYPES) {
    rows.push(['PSL_OUTSIDE_APP_ACCOUNT_TYPES', t.id, pick(form.outsideAccountTypes.includes(t.id)), 'MULTIPLE_CHOICE', `How are these accounts created?/${t.label}`]);
  }
  rows.push(['PSL_OUTSIDE_APP_ACCOUNT_TYPE_SPECIFY', '', form.outsideAccountTypeOther, 'MAYBE_REQUIRED', 'Describe how these accounts are created']);
  for (const c of DATA_CATEGORIES) {
    for (const t of c.types) {
      rows.push([c.id, t.id, b(t.id in form.types), 'MULTIPLE_CHOICE', `${c.label}/${t.label}`]);
    }
  }
  for (const t of ALL_DATA_TYPES) rows.push(...usageRows(t, form.types[t.id]));
  return [HEADER, ...rows.map((r) => r.map(quote).join(','))].join('\n') + '\n';
}

// RFC 4180 reader: a comma inside quotes is text, a doubled quote is a quote,
// a quoted field may span lines. The console's export needs all three.
export function parseCsv(csv: string): string[][] {
  const records: string[][] = [];
  let fields: string[] = [], field = '', inQuotes = false, sawText = false;
  const endField = () => { fields.push(field); field = ''; };
  const endRecord = () => {
    endField();
    if (sawText || fields.length > 1) records.push(fields);
    fields = []; sawText = false;
  };
  for (let i = 0; i < csv.length; i++) {
    const ch = csv[i]!;
    if (inQuotes) {
      if (ch === '"') {
        if (csv[i + 1] === '"') { field += '"'; i++; } else inQuotes = false;
      } else field += ch;
      sawText = true;
      continue;
    }
    if (ch === '"') { inQuotes = true; sawText = true; }
    else if (ch === ',') { endField(); sawText = true; }
    else if (ch === '\n') endRecord();
    else if (ch === '\r') { /* CRLF: the \n ends the record */ }
    else { field += ch; if (ch.trim()) sawText = true; }
  }
  endRecord();
  return records;
}

/**
 * Reads a console export (or our own output) back into the form. Unknown
 * question ids are ignored, so a future export with extra rows still loads;
 * what the form cannot represent is reported in `unknown` so the operator
 * knows the round trip is lossy.
 */
export function fromCsv(csv: string): { form: DataSafetyForm; unknown: string[] } {
  const form = emptyDataSafety();
  const unknown: string[] = [];
  const records = parseCsv(csv);
  const body = records[0]?.[0]?.startsWith('Question ID') ? records.slice(1) : records;
  const isTrue = (v: string) => v.trim().toLowerCase() === 'true';
  const knownTypes = new Set(ALL_DATA_TYPES.map((t) => t.id));
  const purposeIds = new Set<string>(PURPOSES.map((p) => p.id));
  const usage = (typeId: string) => (form.types[typeId] ??= { ...emptyUsage(), collected: false, required: false });
  // Anything the reader touched on a type, so an untouched type stays undeclared.
  const declared = new Set<string>();

  for (const rec of body) {
    const [question = '', response = '', value = ''] = rec;
    if (!question) continue;
    if (question.startsWith('PSL_DATA_USAGE_RESPONSES:')) {
      const [, typeId = '', sub = ''] = question.split(':');
      if (!knownTypes.has(typeId)) { unknown.push(question); continue; }
      if (!isTrue(value)) continue;
      const u = usage(typeId);
      if (sub === 'PSL_DATA_USAGE_COLLECTION_AND_SHARING') {
        if (response === 'PSL_DATA_USAGE_ONLY_COLLECTED') u.collected = true;
        else if (response === 'PSL_DATA_USAGE_ONLY_SHARED') u.shared = true;
      } else if (sub === 'PSL_DATA_USAGE_EPHEMERAL') u.ephemeral = true;
      else if (sub === 'DATA_USAGE_USER_CONTROL') u.required = response === 'PSL_DATA_USAGE_USER_CONTROL_REQUIRED';
      else if (sub === 'DATA_USAGE_COLLECTION_PURPOSE' && purposeIds.has(response)) u.collectionPurposes.push(response as PurposeId);
      else if (sub === 'DATA_USAGE_SHARING_PURPOSE' && purposeIds.has(response)) u.sharingPurposes.push(response as PurposeId);
      else unknown.push(question);
      continue;
    }
    if (question.startsWith('PSL_DATA_TYPES_')) {
      if (!knownTypes.has(response)) { unknown.push(`${question}/${response}`); continue; }
      if (isTrue(value)) declared.add(response);
      continue;
    }
    switch (question) {
      case 'PSL_DATA_COLLECTION_COLLECTS_PERSONAL_DATA': form.collectsOrShares = isTrue(value); break;
      case 'PSL_DATA_COLLECTION_ENCRYPTED_IN_TRANSIT': form.encryptedInTransit = isTrue(value); break;
      case 'PSL_SUPPORTED_ACCOUNT_CREATION_METHODS':
        if (isTrue(value) && response !== 'PSL_ACM_NONE') {
          if (ACCOUNT_METHODS.some((m) => m.id === response)) form.accountMethods.push(response as AccountMethodId);
          else unknown.push(`${question}/${response}`);
        }
        break;
      case 'PSL_ACM_SPECIFY': form.accountMethodOther = value; break;
      case 'PSL_ACCOUNT_DELETION_URL': form.accountDeletionUrl = value; break;
      case 'PSL_SUPPORT_DATA_DELETION_BY_USER':
        if (isTrue(value)) {
          if (DELETION_OPTIONS.some((d) => d.id === response)) form.deletion = response as DeletionAnswer;
          else unknown.push(`${question}/${response}`);
        }
        break;
      case 'PSL_DATA_DELETION_URL': form.dataDeletionUrl = value; break;
      case 'PSL_DATA_COLLECTION_COMPLIES_FAMILY_POLICY': form.compliesFamilyPolicy = isTrue(value); break;
      case 'PSL_INDEPENDENTLY_VALIDATED': form.independentlyValidated = isTrue(value); break;
      case 'PSL_UPI_BADGE_OPT_IN': form.upiBadge = isTrue(value); break;
      case 'PSL_HAS_OUTSIDE_APP_ACCOUNTS': form.outsideAppAccounts = isTrue(value); break;
      case 'PSL_OUTSIDE_APP_ACCOUNT_TYPES':
        if (isTrue(value)) {
          if (OUTSIDE_ACCOUNT_TYPES.some((t) => t.id === response)) form.outsideAccountTypes.push(response as OutsideAccountTypeId);
          else unknown.push(`${question}/${response}`);
        }
        break;
      case 'PSL_OUTSIDE_APP_ACCOUNT_TYPE_SPECIFY': form.outsideAccountTypeOther = value; break;
      default: unknown.push(question);
    }
  }
  // Only types ticked in the data-types section count; usage rows alone do not
  // declare a type, and a declared type with blank usage gets the defaults.
  for (const id of Object.keys(form.types)) if (!declared.has(id)) delete form.types[id];
  for (const id of declared) if (!form.types[id]) form.types[id] = emptyUsage();
  return { form, unknown: [...new Set(unknown)] };
}

/** Short summary for a template list or a collapsed section. */
export function describeDataSafety(form: DataSafetyForm): string {
  if (!form.collectsOrShares) return 'No data collected';
  const names = Object.keys(form.types).map((id) => ALL_DATA_TYPES.find((t) => t.id === id)?.label ?? id);
  const shared = Object.values(form.types).filter((u) => u.shared).length;
  return `${names.length} type${names.length === 1 ? '' : 's'}${shared ? `, ${shared} shared` : ''}: ${names.join(', ')}`;
}
